import type { ReactNode } from 'react';

/**
 * Traductions italiennes du simulateur.
 *
 * LA CLÉ EST LA PHRASE FRANÇAISE, à la lettre. Une clé absente d'ici n'est pas
 * une erreur : l'écran affiche alors le français. Voir `i18n.tsx`.
 *
 * ── LEXIQUE ARRÊTÉ, À FAIRE VALIDER ──────────────────────────────────────────
 *
 * Ces choix n'ont PAS été validés par un locuteur du métier. Ils tiennent du
 * vocabulaire portuaire italien courant et du glossaire déjà écrit dans
 * `docs/cadrage-interoperabilite.md`, mais le programme Interreg Marittimo a des
 * partenaires italiens : c'est à eux de trancher, pas à nous.
 *
 *   remorque .................. semirimorchio   (ro-ro non accompagné)
 *   tracteur portuaire ........ ralla           (terme relevé auprès des
 *                                                partenaires, §glossaire)
 *   attelage / dételage ....... aggancio / sgancio
 *   dépose .................... deposito
 *   débarquement .............. sbarco
 *   escale .................... scalo
 *   plan de chargement ........ piano di carico
 *   zone de contrôle .......... zona di controllo
 *   place (de parc) ........... stallo
 *   immatriculation ........... targa
 *   référentiel ............... anagrafica
 *   rapprochement ............. riconciliazione
 *   consolidation ............. consolidamento
 *   boîtier (UTE) ............. unità di bordo
 *   trame ..................... pacchetto
 *   lot ....................... lotto
 *   graine .................... seme
 *   dérive / gigue ............ deriva / jitter
 *   revue humaine ............. revisione umana
 *
 * Les états du moteur — « confirmed », « qr_only », « external_only »,
 * « conflict », « unidentified », « emission_rejected » — ne se traduisent PAS :
 * ce sont les valeurs mêmes que l'API livre, et les voir en italien à l'écran
 * puis en anglais dans la réponse ferait croire à deux choses différentes.
 */

export const TEXTES: Record<string, string> = {
  Aide: 'Aiuto',
  'Mode d’emploi': 'Manuale d’uso',
  'S’ouvre dans un nouvel onglet': 'Si apre in una nuova scheda',
  'Ouvrir une escale': 'Apri uno scalo',
  '{depart} — {navire} — {n} unités': '{depart} — {navire} — {n} unità',
  'Escale introuvable': 'Scalo non trovato',
  '— Réglage manuel —': '— Regolazione manuale —',
  'UNICA — Università degli Studi di Cagliari, chef de file':
    'UNICA — Università degli Studi di Cagliari, capofila',
  'EPCIC — Établissement public du commerce et de l’industrie de Corse':
    'EPCIC — Ente pubblico del commercio e dell’industria della Corsica',
  'Interreg Marittimo Italie-France — cofinancé par l’Union européenne — EASY2LOG':
    'Interreg Marittimo Italia-Francia — cofinanziato dall’Unione europea — EASY2LOG',
  // ── Connexion ──────────────────────────────────────────────────────────────
  'Même compte que l’administration. Les commandes du simulateur passent par le serveur, qui seul détient le jeton du worker de simulation — la page n’en contient aucun.':
    'Stesso account dell’amministrazione. I comandi del simulatore passano dal server, l’unico a detenere il token del worker di simulazione: la pagina non ne contiene alcuno.',
  Courriel: 'E-mail',
  'Mot de passe': 'Password',
  'Connexion…': 'Accesso in corso…',
  'Se connecter': 'Accedi',

  // ── Scénario ───────────────────────────────────────────────────────────────
  'Simulateur de débarquement': 'Simulatore di sbarco',
  'Scénario — étape 1 sur 3': 'Scenario — passo 1 di 3',
  Tracteurs: 'Ralle',
  Remorques: 'Semirimorchi',

  'Plan de chargement': 'Piano di carico',
  'Le fichier du service fret — .xls ou .csv. Il remplace les plaques fabriquées par les immatriculations réelles, dans l’ordre de débarquement prédit.':
    'Il file del servizio merci — .xls o .csv. Sostituisce le targhe generate con quelle reali, nell’ordine di sbarco previsto.',
  'lecture en cours…': 'lettura in corso…',
  'Échec du dépôt :': 'Caricamento non riuscito:',
  'navire inconnu': 'nave sconosciuta',
  '{retenues} remorques retenues sur {lues} unités lues':
    '{retenues} semirimorchi selezionati su {lues} unità lette',
  '{n} sans immatriculation': '{n} senza targa',
  'Source {format}, horodatage à la {precision} : {n} remorques restent à égalité et n’ont pas de rang. Un fichier .xls les départage.':
    'Origine {format}, marcatura temporale al {precision}: {n} semirimorchi restano a pari merito e non hanno un ordine. Un file .xls li distingue.',
  'Écrit en base : {n} remorques au référentiel.':
    'Scritto in banca dati: {n} semirimorchi in anagrafica.',
  'Rien n’est encore écrit. Sans écriture, ces remorques seront inconnues du référentiel et le run ne produira que des provisoires.':
    'Non è ancora stato scritto nulla. Senza scrittura questi semirimorchi resteranno sconosciuti all’anagrafica e il run produrrà solo provvisori.',
  'Écrit en base': 'Scritto in banca dati',
  'Écrire en base': 'Scrivi in banca dati',
  'Retirer le manifeste': 'Rimuovi il piano di carico',
  minute: 'minuto',
  seconde: 'secondo',

  Graine: 'Seme',
  'À graine identique, la simulation produit exactement les mêmes événements. C’est ce qui rend un débarquement rejouable.':
    'A parità di seme la simulazione produce esattamente gli stessi eventi. È questo a rendere uno sbarco ripetibile.',

  'L’opérateur de la compagnie valide les sorties':
    'L’operatore della compagnia convalida le uscite',
  'Seul chemin qui produit « confirmed »': 'Unico percorso che produce « confirmed »',
  'Sans elle, les sorties opérateur ne partent pas et la consolidation plafonne à « qr_only ». La clé est lue par le serveur : la page n’en contient aucune.':
    'Senza di essa le uscite operatore non partono e il consolidamento si ferma a « qr_only ». La chiave è letta dal server: la pagina non ne contiene alcuna.',
  'Éprouver une autre clé': 'Provare un’altra chiave',
  'Clé d’API de la source opérateur': 'Chiave API della sorgente operatore',
  'laisser vide : le serveur utilise la sienne': 'lasciare vuoto: il server usa la propria',
  'Saisie de dépannage. Elle n’est ni conservée, ni journalisée, ni versionnée, et elle est effacée dès la création du run.':
    'Inserimento di emergenza. Non viene conservato, né registrato, né versionato, ed è cancellato alla creazione del run.',
  'Créer le run': 'Crea il run',

  // ── Cas métier ─────────────────────────────────────────────────────────────
  'Cas métier': 'Casi operativi',
  'Un bouton applique un préréglage ENTIER, aligné sur le scénario du test correspondant.':
    'Un pulsante applica una preimpostazione COMPLETA, allineata allo scenario del test corrispondente.',

  'Tout se passe bien': 'Tutto va bene',
  "QR lu à chaque fois, opérateur qui valide chaque sortie. Les deux sources concordent : c'est le seul chemin qui produit « confirmed ».":
    'QR letto ogni volta, operatore che convalida ogni uscita. Le due sorgenti concordano: è l’unico percorso che produce « confirmed ».',
  "Le QR n'est pas lu": 'Il QR non viene letto',
  "Aucune lecture embarquée. Seule la validation de l'opérateur identifie : la décision tombe à « external_only ».":
    'Nessuna lettura a bordo. Solo la convalida dell’operatore identifica: la decisione scende a « external_only ».',
  "L'opérateur ne valide pas": 'L’operatore non convalida',
  "La compagnie ne saisit rien. Le QR reste seul : « qr_only ». C'est l'état observé en production quand la source opérateur n'écrit pas.":
    'La compagnia non inserisce nulla. Resta il solo QR: « qr_only ». È lo stato osservato in produzione quando la sorgente operatore non scrive.',
  'Les deux sources se contredisent': 'Le due sorgenti si contraddicono',
  "L'opérateur saisit une autre remorque que celle qu'a lue le QR. Le moteur ne tranche JAMAIS : « conflict », aucune remorque désignée, revue humaine exigée (invariant nº3).":
    'L’operatore inserisce un semirimorchio diverso da quello letto dal QR. Il motore non decide MAI: « conflict », nessun semirimorchio designato, revisione umana obbligatoria (invariante nº3).',
  'Le QR est mal lu': 'Il QR viene letto male',
  'Un caractère confondu (5↔6, 8↔0, 1↔7) : la valeur lue ne désigne aucune remorque du référentiel. Le QR devient inexploitable plutôt que trompeur.':
    'Un carattere confuso (5↔6, 8↔0, 1↔7): il valore letto non designa alcun semirimorchio in anagrafica. Il QR diventa inutilizzabile anziché ingannevole.',
  'Le signal revient tard': 'Il segnale torna tardi',
  'Vingt secondes pour retrouver un fix à la sortie du navire. Le passage au poste de contrôle doit rester détectable malgré le trou.':
    'Venti secondi per ritrovare il fix all’uscita dalla nave. Il passaggio alla postazione di controllo deve restare rilevabile nonostante il vuoto.',
  'Position imprécise au contrôle': 'Posizione imprecisa al controllo',
  "25 m de précision annoncée, au-delà des 15 m tolérés par la zone de contrôle. La position est signalée douteuse ; le passage n'est jamais nié.":
    '25 m di precisione dichiarata, oltre i 15 m tollerati dalla zona di controllo. La posizione è segnalata come dubbia; il passaggio non viene mai negato.',
  'Le réseau tombe': 'La rete cade',
  "Quatre minutes de coupure générale, puis deux minutes sur le seul TRACTOR-01. Les mesures s'accumulent réellement dans le tampon et repartent avec leur horodatage d'origine : le résultat métier doit être identique à celui d'une liaison parfaite.":
    'Quattro minuti di interruzione generale, poi due minuti sul solo TRACTOR-01. Le misure si accumulano davvero nel buffer e ripartono con la loro marcatura temporale d’origine: il risultato operativo deve essere identico a quello di un collegamento perfetto.',
  'Doublons et désordre': 'Duplicati e disordine',
  "Chaque lot est rejoué à l'identique et un lot sur trois est retenu puis réémis après le suivant. Les event_id étant inchangés, le serveur doit produire zéro ligne en double et ne jamais se fier à l'ordre de réception.":
    'Ogni lotto viene ritrasmesso identico e un lotto su tre viene trattenuto e poi rinviato dopo il successivo. Essendo gli event_id invariati, il server non deve produrre alcuna riga duplicata e non deve mai fidarsi dell’ordine di ricezione.',
  'Débarquement saturé': 'Sbarco saturo',
  "Une remorque libérée toutes les 8 s, six tracteurs, aucun QR : plusieurs passages tombent dans la même fenêtre. Le moteur doit refuser d'attribuer plutôt que deviner — une revue coûte du temps, une identification fausse coûte une remorque introuvable.":
    'Un semirimorchio liberato ogni 8 s, sei ralle, nessun QR: più passaggi cadono nella stessa finestra. Il motore deve rifiutare di attribuire anziché indovinare — una revisione costa tempo, un’identificazione errata costa un semirimorchio introvabile.',
  'Remorques hors référentiel': 'Semirimorchi fuori anagrafica',
  "Aucune remorque n'est au référentiel, donc aucune ne porte de QR : rien à lire. Le débarquement doit s'achever quand même — une remorque non identifiée ne bloque jamais (§24).":
    'Nessun semirimorchio è in anagrafica, quindi nessuno porta un QR: nulla da leggere. Lo sbarco deve concludersi lo stesso — un semirimorchio non identificato non blocca mai (§24).',
  'Tracteur en panne': 'Ralla in avaria',
  "TRACTOR-03 tombe hors service au bout de deux minutes et demie. Il n'est jamais immobilisé avec une remorque attelée, et les autres achèvent le débarquement.":
    'TRACTOR-03 si guasta dopo due minuti e mezzo. Non resta mai fermo con un semirimorchio agganciato e gli altri portano a termine lo sbarco.',
  'Lecture QR anticipée': 'Lettura QR anticipata',
  "Le tracteur scanne dans la cale la remorque du cycle SUIVANT, 45 à 180 s d'avance, et ne la relit pas à l'attelage. La lecture tombe alors hors de la fenêtre de son propre cycle et dans celle du précédent. Mesuré sur 5 graines, 12 remorques, 3 tracteurs, sans opérateur : 0 identification fausse sans ce réglage, 11 sur 60 avec. C'est le comportement réel qui justifie la fenêtre de 120 s — et le seul moyen pour le banc de VOIR le défaut A1, jusqu'ici démontrable au niveau du moteur et invisible en recette.":
    'La ralla scansiona in stiva il semirimorchio del ciclo SUCCESSIVO, con 45-180 s di anticipo, e non lo rilegge all’aggancio. La lettura cade così fuori dalla finestra del proprio ciclo e dentro quella del precedente. Misurato su 5 semi, 12 semirimorchi, 3 ralle, senza operatore: 0 identificazioni errate senza questa impostazione, 11 su 60 con. È il comportamento reale che giustifica la finestra di 120 s — e l’unico modo per il banco di VEDERE il difetto A1, finora dimostrabile a livello di motore e invisibile in collaudo.',

  // ── Réglages fins ──────────────────────────────────────────────────────────
  'Réglages fins': 'Regolazioni fini',
  'Lecture du QR à bord': 'Lettura del QR a bordo',
  'Le QR est lu': 'Il QR viene letto',
  'Lecture erronée': 'Lettura errata',
  'Un caractère confondu (5↔6, 8↔0, 1↔7). La valeur lue ne désigne alors aucune remorque du référentiel.':
    'Un carattere confuso (5↔6, 8↔0, 1↔7). Il valore letto non designa allora alcun semirimorchio in anagrafica.',
  'Lectures rapprochées en double': 'Letture ravvicinate doppie',
  'Lecture après l’attelage': 'Lettura dopo l’aggancio',
  'Lecture anticipée de la remorque suivante': 'Lettura anticipata del semirimorchio successivo',
  'Le tracteur scanne dans la cale la remorque qu’il ira chercher ensuite, 45 à 180 s d’avance. La lecture tombe alors dans deux fenêtres d’attelage voisines.':
    'La ralla scansiona in stiva il semirimorchio che andrà a prendere dopo, con 45-180 s di anticipo. La lettura cade così in due finestre di aggancio contigue.',

  'Saisie de l’opérateur': 'Inserimento dell’operatore',
  'L’opérateur valide la sortie': 'L’operatore convalida l’uscita',
  'Il saisit une AUTRE remorque': 'Inserisce un ALTRO semirimorchio',
  'Les deux sources se contredisent : le moteur ne tranche jamais, il exige une revue humaine.':
    'Le due sorgenti si contraddicono: il motore non decide mai, esige una revisione umana.',
  'Ponctuation différente': 'Punteggiatura diversa',
  '« AB 123 CD » pour « AB-123-CD ». Doit rester la même remorque.':
    '« AB 123 CD » per « AB-123-CD ». Deve restare lo stesso semirimorchio.',
  'Sortie saisie deux fois': 'Uscita inserita due volte',
  'La source indique le poste de contrôle': 'La sorgente indica la postazione di controllo',
  'La source transmet un rang de validation': 'La sorgente trasmette un ordine di convalida',

  'Horloge de la compagnie': 'Orologio della compagnia',
  'Dérive constante': 'Deriva costante',
  'Mesuré au banc : sans QR lu, au-delà de ±30 s les premières identifications FAUSSES apparaissent ; à ±40 s elles se comptent par dizaines et les justes tombent à zéro. Avec le QR lu, aucune dérive ne produit d’identification fausse — elle produit du conflit.':
    'Misurato al banco: senza QR letto, oltre ±30 s compaiono le prime identificazioni ERRATE; a ±40 s si contano a decine e quelle corrette scendono a zero. Con il QR letto, nessuna deriva produce un’identificazione errata — produce conflitto.',
  'Gigue de saisie': 'Jitter di inserimento',
  'Tirée à chaque sortie, elle se compense en moyenne — contrairement à la dérive.':
    'Estratto a ogni uscita, in media si compensa — a differenza della deriva.',

  'Signal GNSS': 'Segnale GNSS',
  'Reprise à la sortie du navire': 'Ripresa all’uscita dalla nave',
  'Échec de reprise': 'Mancata ripresa',
  'Le tracteur roule sans position jusqu’au cycle suivant.':
    'La ralla procede senza posizione fino al ciclo successivo.',
  'Précision dégradée': 'Precisione degradata',
  'Précision annoncée quand elle est dégradée': 'Precisione dichiarata quando è degradata',
  'Au-delà de 15 m, les points sont écartés du calcul de position stabilisée — et conservés avec leur motif de rejet.':
    'Oltre i 15 m i punti sono esclusi dal calcolo della posizione stabilizzata — e conservati con il motivo dello scarto.',
  'Variabilité de la précision': 'Variabilità della precisione',
  '0 : précision constante et bruit blanc, le modèle d’origine. Au-dessus, la précision bouge d’un point à l’autre et l’erreur garde sa direction une vingtaine de secondes, comme un vrai récepteur.':
    '0: precisione costante e rumore bianco, il modello originale. Sopra, la precisione varia da un punto all’altro e l’errore mantiene la sua direzione per una ventina di secondi, come un ricevitore reale.',

  'Réseau 5G du port': 'Rete 5G del porto',
  'Coupure — début': 'Interruzione — inizio',
  'Coupure — durée': 'Interruzione — durata',
  'Une coupure ne perd rien : les mesures s’accumulent dans le tampon avec leur horodatage d’origine et repartent telles quelles.':
    'Un’interruzione non perde nulla: le misure si accumulano nel buffer con la loro marcatura temporale d’origine e ripartono così come sono.',
  'Lot rejoué à l’identique': 'Lotto ritrasmesso identico',
  'Mêmes event_id : le serveur doit produire zéro ligne en double.':
    'Stessi event_id: il server non deve produrre alcuna riga duplicata.',
  'Lot reçu dans le désordre': 'Lotto ricevuto in disordine',

  'Cadence du débarquement': 'Cadenza dello sbarco',
  'Une remorque libérée toutes les': 'Un semirimorchio liberato ogni',
  'La cadence ne déplace pas le seuil de dérive tolérable, elle abaisse le rendement : à une remorque toutes les 25 s, plusieurs tombent dans la même fenêtre et le moteur refuse d’arbitrer.':
    'La cadenza non sposta la soglia di deriva tollerabile, abbassa la resa: con un semirimorchio ogni 25 s, più d’uno cade nella stessa finestra e il motore rifiuta di arbitrare.',

  'Aléas du terrain': 'Imprevisti sul campo',
  'Elles ne portent aucun QR : rien à lire. Seul l’opérateur peut les identifier.':
    'Non portano alcun QR: nulla da leggere. Solo l’operatore può identificarli.',
  'Parc déjà occupé à l’arrivée': 'Piazzale già occupato all’arrivo',
  'Occupation du parc tirée au hasard à chaque run, entre 5 et 35 %':
    'Occupazione del piazzale estratta a caso a ogni simulazione, tra il 5 e il 35 %',
  'Remorques des escales précédentes, sur des places tirées au hasard. Le moteur laisse toujours assez de places libres pour le navire. Il ne lève AUCUNE anomalie : l’anomalie « slot_already_occupied » du §19.18 vient des options de RAPPROCHEMENT, toujours vides en production.':
    'Semirimorchi degli scali precedenti, su stalli estratti a caso. Il motore lascia sempre abbastanza stalli liberi per la nave. Non solleva ALCUNA anomalia: l’anomalia « slot_already_occupied » del §19.18 proviene dalle opzioni di RICONCILIAZIONE, sempre vuote in produzione.',
  'Le chauffeur prend une place libre au hasard, sans la poser au centre':
    'L’autista prende uno stallo libero a caso, senza posarlo al centro',
  'Le dernier tracteur tombe en panne après 2 min 30': 'L’ultima ralla si guasta dopo 2 min 30',

  'Horloge des boîtiers': 'Orologio delle unità di bordo',
  'Dérive de TRACTOR-01': 'Deriva di TRACTOR-01',
  'Appliquée à la MESURE, comme le ferait un boîtier dont l’horloge est fausse. Un seul boîtier dérive : décaler les trois reviendrait à décaler l’horloge du port.':
    'Applicata alla MISURA, come farebbe un’unità di bordo con l’orologio sbagliato. Deriva una sola unità: spostarne tre equivarrebbe a spostare l’orologio del porto.',

  'Seize des vingt-deux cas du §19 s’obtiennent ici. Cinq exigent un levier ailleurs.':
    'Sedici dei ventidue casi del §19 si ottengono qui. Cinque richiedono una leva altrove.',

  // ── Boîtiers ───────────────────────────────────────────────────────────────
  'Boîtiers — étape 2 sur 3': 'Unità di bordo — passo 2 di 3',
  'Enrôler AVANT de démarrer': 'Registrare PRIMA di avviare',
  'Le simulateur emprunte le même chemin qu’un boîtier réel, contrôle d’identité compris. Tant que ses clés ne sont pas déclarées au registre, ses trames sont refusées et rien n’arrive en base.':
    'Il simulatore percorre la stessa via di un’unità di bordo reale, controllo d’identità compreso. Finché le sue chiavi non sono dichiarate nel registro, i suoi pacchetti sono rifiutati e nulla arriva in banca dati.',
  'Aucune clé trouvée — le run n’a pas été créé correctement.':
    'Nessuna chiave trovata — il run non è stato creato correttamente.',
  '{n} boîtier(s) enrôlé(s) : {liste}. Compter environ une minute de propagation avant de démarrer.':
    '{n} unità di bordo registrate: {liste}. Prevedere circa un minuto di propagazione prima di avviare.',
  'Enrôler les boîtiers': 'Registra le unità di bordo',
  'Un seul run enrôlé à la fois.': 'Un solo run registrato per volta.',

  // ── Pilotage ───────────────────────────────────────────────────────────────
  'Pilotage — étape 3 sur 3': 'Comando — passo 3 di 3',
  'Démarrer avant l’enrôlement fait refuser toutes les trames. Elles sont désormais conservées et réessayées, mais dans la limite de trois tentatives : enrôler d’abord reste la bonne séquence.':
    'Avviare prima della registrazione fa rifiutare tutti i pacchetti. Ora vengono conservati e ritentati, ma entro un limite di tre tentativi: registrare prima resta la sequenza corretta.',
  Reprendre: 'Riprendi',
  Démarrer: 'Avvia',
  Pause: 'Pausa',
  Arrêter: 'Ferma',
  'Remise à zéro': 'Azzeramento',
  Vitesse: 'Velocità',
  'Le moteur découpe chaque tick en pas fixes d’une seconde : à graine égale, ×1 et ×20 produisent exactement les mêmes mesures, aux mêmes horodatages. Accélérer ne change que le temps réel écoulé.':
    'Il motore suddivide ogni tick in passi fissi di un secondo: a parità di seme, ×1 e ×20 producono esattamente le stesse misure, con le stesse marcature temporali. Accelerare cambia solo il tempo reale trascorso.',

  // ── État ───────────────────────────────────────────────────────────────────
  État: 'Stato',
  Statut: 'Stato',
  Flux: 'Flusso',
  'Heure simulée': 'Ora simulata',
  'Événements produits': 'Eventi prodotti',
  'Acceptés par le serveur': 'Accettati dal server',
  Rejetés: 'Respinti',
  'Sorties opérateur': 'Uscite operatore',
  '{acceptees} acceptées, {rejetees} rejetées': '{acceptees} accettate, {rejetees} respinte',
  '{n} non envoyées': '{n} non inviate',
  'Lots envoyés': 'Lotti inviati',
  'Places occupées': 'Stalli occupati',
  'Restant à débarquer': 'Ancora da sbarcare',
  Rapprochement: 'Riconciliazione',
  'pas encore déclenché': 'non ancora avviata',

  'La consolidation n’a pas abouti.': 'Il consolidamento non è andato a buon fine.',
  '« déclenché » n’est pas un échec : la consolidation a été acceptée et se poursuit en arrière-plan. C’est le mode normal, et elle dure environ une minute.':
    '« déclenché » non è un fallimento: il consolidamento è stato accettato e prosegue in background. È il funzionamento normale e dura circa un minuto.',
  'Des trames ont été refusées. L’historique en donne le statut et le corps d’erreur, ligne « emission_rejected ». Cause la plus fréquente : boîtiers non enrôlés, ou propagation du registre encore en cours.':
    'Alcuni pacchetti sono stati rifiutati. Lo storico ne riporta lo stato e il corpo dell’errore, riga « emission_rejected ». Causa più frequente: unità di bordo non registrate, o propagazione del registro ancora in corso.',
  'Des sorties opérateur ont été refusées — ligne « EXTERNAL-APP » de l’historique. Ce chemin s’authentifie par clé partagée : clé absente du worker, périmée, ou posée avec un saut de ligne.':
    'Alcune uscite operatore sono state rifiutate — riga « EXTERNAL-APP » dello storico. Questa via si autentica con chiave condivisa: chiave assente dal worker, scaduta, o inserita con un a capo.',
  'Ce run a été créé sans source opérateur.': 'Questo run è stato creato senza sorgente operatore.',
  'Le moteur a produit {n} sortie(s) qui ne sont JAMAIS parties, et la consolidation ne pourra pas conclure « confirmed ». Le serveur détient bien la clé : il n’y a rien à déployer. Recréer le run avec la case « L’opérateur de la compagnie valide les sorties » cochée — elle est lue à la création et ne se rattrape pas en cours de route.':
    'Il motore ha prodotto {n} uscite che non sono MAI partite, e il consolidamento non potrà concludere « confirmed ». Il server possiede la chiave: non c’è nulla da distribuire. Ricreare il run con la casella « L’operatore della compagnia convalida le uscite » selezionata — viene letta alla creazione e non si recupera in corso d’opera.',
  'Aucune clé de source opérateur sur le serveur.':
    'Nessuna chiave della sorgente operatore sul server.',
  'Le moteur a produit {n} sortie(s) qui ne sont JAMAIS parties : la consolidation ne pourra pas conclure « confirmed ». Poser le secret SIMULATION_EXTERNAL_API_KEY sur le worker de simulation (wrangler secret bulk, jamais secret put), ou saisir une clé dans « Éprouver une autre clé » à la création du run.':
    'Il motore ha prodotto {n} uscite che non sono MAI partite: il consolidamento non potrà concludere « confirmed ». Impostare il segreto SIMULATION_EXTERNAL_API_KEY sul worker di simulazione (wrangler secret bulk, mai secret put), oppure inserire una chiave in « Provare un’altra chiave » alla creazione del run.',

  // ── Tracteurs, carte, historique ───────────────────────────────────────────
  'sans fix': 'senza fix',
  'hors ligne ({n})': 'fuori linea ({n})',
  'Afficher les codes de place': 'Mostra i codici degli stalli',
  'Vue aérienne de lecture — {n} %': 'Vista aerea di lettura — {n} %',
  Historique: 'Storico',
  'Fond de plan fictif — les coordonnées ne sont pas réelles':
    'Sfondo cartografico fittizio — le coordinate non sono reali',

  // ── Erreurs ────────────────────────────────────────────────────────────────
  'Affichage interrompu': 'Visualizzazione interrotta',
  'Les données sont intactes : seule leur mise en forme a échoué. Recharger la page ; si l’erreur persiste, elle est reproductible et donc corrigeable.':
    'I dati sono intatti: solo la loro formattazione è fallita. Ricaricare la pagina; se l’errore persiste è riproducibile e quindi correggibile.',
  Recharger: 'Ricarica',
  'Erreur inconnue': 'Errore sconosciuto',
  'Identifiants refusés.': 'Credenziali rifiutate.',
  "L'authentification n'est pas configurée (URL ou clé publique absente).":
    'L’autenticazione non è configurata (URL o chiave pubblica assenti).',
  'Création du run refusée': 'Creazione del run rifiutata',
  'Dépôt refusé': 'Caricamento rifiutato',
  'Commande refusée': 'Comando rifiutato',
  'Enrôlement refusé': 'Registrazione rifiutata',
  'Instantané indisponible': 'Istantanea non disponibile',
};

/**
 * Blocs à balisage. Le français correspondant est écrit sur place dans le JSX ;
 * ici on donne l'italien sous la même clé. Une clé absente ⇒ le français reste.
 */
export const RICHES: Record<string, ReactNode> = {
  manifeste: (
    <>
      Il file del servizio merci — <code>.xls</code> o <code>.csv</code>. Sostituisce le targhe
      generate con quelle reali, nell’ordine di sbarco previsto.
    </>
  ),

  'source-operateur': (
    <>
      <strong>Unico percorso che produce « confirmed »</strong>, e si decide qui: la casella è letta
      alla creazione del run.
    </>
  ),

  'cas-hors-portee': (
    <>
      <strong>§19.17</strong> due varianti dello stesso identificativo — il test inserisce una
      seconda lettura nel lotto. <strong>§19.18</strong> stallo già occupato — l’anomalia proviene
      dalle opzioni di riconciliazione, non dal cursore qui sopra. <strong>§19.20</strong> deposito
      senza posizione affidabile — le osservazioni sono degradate dopo la simulazione.{' '}
      <strong>§19.21</strong> correzione manuale — è un atto di amministrazione.{' '}
      <strong>§19.22</strong> ricalcolo dopo modifica delle regole — cambiano le opzioni di
      riconciliazione, non lo scenario.
    </>
  ),

  'enroler-avant': (
    <>
      <strong>Registrare PRIMA di avviare</strong>, altrimenti l’inizio dello sbarco parte con dei
      ritentativi. Le chiavi esistono fin dalla creazione del run.
    </>
  ),

  'un-seul-run': (
    <>
      <strong>Un solo run registrato per volta.</strong> Registrarne un secondo rende i pacchetti
      del primo « signature_invalid ».
    </>
  ),

  'registre-boitiers': (
    <>
      Il registro è indicizzato per identificativo di unità di bordo —{' '}
      <code>device:TRACTOR-01</code> fino a <code>TRACTOR-03</code> — e tutti i run usano gli
      stessi. Non è quindi un guasto: è la chiave che è cambiata.
    </>
  ),

  'consolidation-echec': (
    <>
      <strong>Il consolidamento non è andato a buon fine.</strong> Lo sbarco può essere terminato
      senza che i movimenti siano in banca dati: ciò che l’API di uscita consegna a UNICA resta
      allora la fotografia precedente, senza che nulla lo segnali. Motivo qui sopra.
    </>
  ),

  'run-sans-operateur': (
    <>
      Questo run gira <strong>senza</strong> sorgente operatore: non è stata fornita alcuna chiave e
      il server non ne possiede. L’identificazione si fermerà a « qr_only ».
    </>
  ),

  'fond-de-plan': (
    <>
      Sfondo cartografico non disponibile. Eseguire <code>node scripts/prepare-basemap.mjs</code>.
    </>
  ),
};
